import { ApiClientError } from "./api-client";
import { coachingApi, coachingAuthApi } from "./coaching-api";
import { type Requester, readLiveAuthProfile, requestContract } from "./coaching-services-transport";
import {
  isDtoShapedResult,
  isLiveOtpVerifySuccess,
  toOtpRequestResult,
  toProfileUpdateResult
} from "./coaching-live-auth";
import { emitPlatformEvents } from "../lib/platform-events";
import type {
  CoachingProfileDto,
  OtpRequest,
  OtpRequestResult,
  OtpVerifyRequest,
  OtpVerifyResult,
  ProfileUpdateRequest,
  ProfileUpdateResult
} from "./coaching-dtos";

export type CoachingAuthService = ReturnType<typeof createCoachingAuthService>;

export function createCoachingAuthService(requester: Requester, platformAuthRequester?: Requester) {
  // OTP send/verify and logout live on the platform auth lane (api-agents.vjal.ai),
  // not the coaching product host; mock mode keeps a single in-memory requester.
  const authRequester = platformAuthRequester ?? (requester === coachingApi ? coachingAuthApi : requester);

  const auth = {
    me: async (): Promise<CoachingProfileDto> => {
      return readLiveAuthProfile(requester);
    },
    requestOtp: async (body: OtpRequest, idempotencyKey?: string): Promise<OtpRequestResult> => {
      try {
        return toOtpRequestResult(await requestContract<unknown>(authRequester, "auth.requestOtp", { body, idempotencyKey }));
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 429) {
          return { ok: false, status: "rate_limited", reference: error.reference ?? undefined };
        }
        throw error;
      }
    },
    verifyOtp: async (body: OtpVerifyRequest, idempotencyKey?: string): Promise<OtpVerifyResult> => {
      let payload: unknown;
      try {
        payload = await requestContract<unknown>(authRequester, "auth.verifyOtp", { body, idempotencyKey });
      } catch (error) {
        if (error instanceof ApiClientError && error.status < 500) {
          return { ok: false, status: "invalid", reference: error.reference ?? undefined };
        }
        throw error;
      }
      if (isLiveOtpVerifySuccess(payload)) {
        // Live verify sets the platform session cookie and returns { success: true };
        // the coaching profile projection is hydrated through /auth/me.
        return { ok: true, status: "verified", profile: await auth.me() };
      }
      return payload as OtpVerifyResult;
    },
    logout: (idempotencyKey?: string) =>
      requestContract<{ ok: boolean }>(authRequester, "auth.logout", { idempotencyKey })
  };

  return auth;
}

export function createProfileService(requester: Requester, auth: CoachingAuthService) {
  return {
    update: async (body: ProfileUpdateRequest, idempotencyKey: string): Promise<ProfileUpdateResult> => {
      try {
        const payload = await requestContract<unknown>(requester, "profile.update", { body, idempotencyKey });
        if (isDtoShapedResult(payload)) return payload as ProfileUpdateResult;
        return toProfileUpdateResult(payload, await auth.me());
      } catch (error) {
        if (error instanceof ApiClientError && (error.status === 400 || error.status === 403)) {
          return { ok: false, status: "validation_error", reference: error.reference ?? undefined };
        }
        throw error;
      }
    }
  };
}

export function createPlatformEventsService() {
  return {
    // Frontend events are emitted ONLY through the configured same-origin
    // proxy (VITE_PLATFORM_EVENTS_PROXY_URL); with no proxy set this no-ops
    // and never posts through the coaching product API. The product-API
    // platformEvents.readiness contract is retained for inventory/backend
    // traceability but is not the browser emission path. The idempotency key
    // is accepted for call-site symmetry and folded into the event metadata.
    readiness: async (body: unknown, idempotencyKey: string): Promise<{ ok: boolean; event_ref?: string }> => {
      const event =
        body && typeof body === "object" && !Array.isArray(body)
          ? { ...(body as Record<string, unknown>), idempotency_key: idempotencyKey }
          : { payload: body, idempotency_key: idempotencyKey };
      const result = await emitPlatformEvents([event]);
      return { ok: result.ok };
    }
  };
}
